import React, { useCallback } from "react";
import { useHistory } from "react-router-dom";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCheckCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { commitMutation, graphql } from "relay-runtime";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { isDesktopApp } from "~/lib/utils";
import {
	chromeStoreLink,
	getFirstUserFacingError,
	isFirefox,
	mozillaStoreLink,
	msTeamsStoreLink,
	zoomStoreLink,
} from "~/lib/utils";
import { environment } from "~/models";
import { Button } from "~/ui";
import { Input, Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import type { RecordLiveMeetingModalMutation } from "~/__generated__/RecordLiveMeetingModalMutation.graphql";

import styles from "./RecordLiveMeetingModal.module.css";

function ModalHeader({ onClose }: { onClose: () => void }) {
	return (
		<Modal.Header onClose={onClose} noBottomBorder>
			<Modal.Title>Record now with meeting link</Modal.Title>
		</Modal.Header>
	);
}

export const recordLiveMeetingMutation = graphql`
	mutation RecordLiveMeetingModalMutation($meetingLink: String!, $location: String!) {
		recordLiveMeeting(meetingLink: $meetingLink, location: $location) {
			callProvider
			eventGuid
		}
	}
`;

type CallData = {
	callProvider: string;
	eventGuid: string;
};

type ModalInnerInputProps = {
	onClose: () => void;
	setCallData: (value: CallData) => void;
};

function ModalInnerInput({ onClose, setCallData }: ModalInnerInputProps) {
	const [link, setLink] = React.useState("");
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);

	const updateLink = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setLink(e.target.value);
		if (e.target.value == "") {
			setError(null);
		}
	}, []);

	const startRecording = useCallback(() => {
		setLoading(true);
		dispatchAnalyticsTrack("Clicked start recording attempt", {
			location: "Paste link",
			platform: isDesktopApp ? "Desktop" : "Browser",
			// eslint-disable-next-line camelcase
			recording_provider: "BOT",
		});

		commitMutation<RecordLiveMeetingModalMutation>(environment, {
			mutation: recordLiveMeetingMutation,
			variables: {
				meetingLink: link,
				location: "Paste Link",
			},
			onCompleted: (response, errors) => {
				setLoading(false);
				if ((errors && errors.length > 0) || !response.recordLiveMeeting) {
					const userFacingError = getFirstUserFacingError(errors);
					if (userFacingError) {
						setError(userFacingError);
					} else {
						setError("Something went wrong");
					}
					dispatchAnalyticsTrack("Clicked start recording failed", {
						location: "Paste link",
						platform: isDesktopApp ? "Desktop" : "Browser",
					});
				} else {
					const { callProvider, eventGuid } = response.recordLiveMeeting;
					setCallData({ callProvider, eventGuid });
					dispatchAnalyticsTrack("Clicked start recording succeeded", {
						location: "Paste link",
						platform: isDesktopApp ? "Desktop" : "Browser",
					});
				}
			},
			onError: e => {
				const userFacingError = getFirstUserFacingError(e);
				if (userFacingError) {
					setError(userFacingError);
				} else {
					setError("Something went wrong");
				}
				dispatchAnalyticsTrack("Clicked start recording failed", {
					location: "Paste link",
					platform: isDesktopApp ? "Desktop" : "Browser",
				});
				setLoading(false);
			},
		});
	}, [link, setCallData]);

	const submitForm = useCallback(
		(e: React.FormEvent<HTMLFormElement>) => {
			startRecording();
			e.preventDefault();
		},
		[startRecording],
	);

	const formId = "recordLiveMeetingForm";

	return (
		<>
			<ModalHeader onClose={onClose} />
			<Modal.Body className={styles.modalBody}>
				<div>
					<div className={styles.modalDescription}>
						If there is no calendar event associated with the link, a new private calendar event will be created for you
						to access the meeting notes and AI recap.
					</div>
					<div>
						<div className={styles.meetingLinkHeader}>Meeting link</div>
						<form onSubmit={submitForm} id={formId}>
							<Input
								className={cx(styles.meetingLinkInput, { [styles.error]: !!error })}
								autoFocus
								onChange={updateLink}
								placeholder="Eg. https://meet.google.com/abc-defg-hij"
								name="meeting_link"
							></Input>
						</form>
						{error ? (
							<div className={styles.errorText}>{error}</div>
						) : (
							<div className={styles.meetingLinkSupportingText}>Record Google Meet, Zoom and MS Teams meetings</div>
						)}
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer>
				{loading && <LoadingSpinner />}
				<>
					<Button onClick={onClose}>Cancel</Button>
					<Button variant="primary" disabled={link === ""} onClick={startRecording} form={formId}>
						Start recording
					</Button>
				</>
			</Modal.Footer>
		</>
	);
}

type ModalInnerSuccessProps = {
	onClose: () => void;
	callData: CallData;
};

function ModalInnerSuccess({ onClose, callData }: ModalInnerSuccessProps) {
	const history = useHistory();

	const { eventGuid, callProvider } = callData;

	const redirectToMeeting = useCallback(() => {
		if (window.electron && window.electron.navigateCompanionMode) {
			window.electron.navigateCompanionMode(`/companion-mode/event/${eventGuid}/`);
		} else {
			history.push(`/meetings/${eventGuid}/`);
		}
		onClose();
	}, [onClose, history, eventGuid]);

	const proTipText = "Pro tip: You can start a recording directly in your meeting with";

	const meetContent = (
		<div>
			{proTipText}{" "}
			<a href={isFirefox ? mozillaStoreLink : chromeStoreLink} target="_blank" rel="noreferrer">
				Fellow&apos;s {isFirefox ? "browser add-on" : "Chrome extension"}
			</a>{" "}
			for Google Meet.
		</div>
	);

	const zoomContent = (
		<div>
			{proTipText}{" "}
			<a href={zoomStoreLink} target="_blank" rel="noreferrer">
				Fellow&apos;s app for Zoom
			</a>
			.
		</div>
	);

	const teamsContent = (
		<div>
			{proTipText}{" "}
			<a href={msTeamsStoreLink} target="_blank" rel="noreferrer">
				Fellow&apos;s app for MS Teams
			</a>
			.
		</div>
	);

	return (
		<>
			<ModalHeader onClose={onClose} />
			<Modal.Body className={styles.modalBody}>
				<>
					<div className={styles.successMainContainer}>
						<FontAwesomeIcon icon={faCheckCircle} className={styles.successCheckIcon} />
						The Note Taker will join your meeting soon
					</div>
					<div className={styles.successSupportingContainer}>
						<FontAwesomeIcon icon={faInfoCircle} className={styles.successInfoIcon} />
						{callProvider === "GOOGLE" && meetContent}
						{callProvider === "ZOOM" && zoomContent}
						{callProvider === "TEAMS" && teamsContent}
					</div>
				</>
			</Modal.Body>
			<Modal.Footer>
				<>
					<Button onClick={onClose}>Close</Button>
					<Button autoFocus variant="primary" onClick={redirectToMeeting}>
						Open note
					</Button>
				</>
			</Modal.Footer>
		</>
	);
}

type RecordLiveMeetingModalProps = {
	onClose: () => void;
};

export default function RecordLiveMeetingModal({ onClose }: RecordLiveMeetingModalProps) {
	const [callData, setCallData] = React.useState<CallData | undefined>(undefined);

	return (
		<Modal onClose={onClose}>
			{callData ? (
				<ModalInnerSuccess onClose={onClose} callData={callData} />
			) : (
				<ModalInnerInput onClose={onClose} setCallData={setCallData} />
			)}
		</Modal>
	);
}
